import * as React from 'react'
import {
  useGetActiveTabularConfigBuildQuery,
  useGetLatestTabularConfigBuildQuery,
  useGetTabularConfigBuildQuery,
  useListIngestMappingConfigsQuery,
  type IngestMappingConfigSummary,
  type TabularConfigBuildDetail
} from '@axilon-apps/domains-ontology-ui-state'
import { useSearchParams } from 'react-router-dom'
import { isBuildInFlight, resolveConfigView, type ConfigView } from './util/configView'

export interface ConfigPageData {
  view: ConfigView | null
  versions: IngestMappingConfigSummary[]
  /** The build the page is about (URL build, or the one in flight on the config route). */
  build: TabularConfigBuildDetail | null
  loading: boolean
  error: unknown
  /** True while the builder is still working. */
  live: boolean
  refetchBuild: () => Promise<unknown>
  refetchAll: () => void
}

/**
 * Three queries and the URL → one `ConfigView`. Builds refetch on `jobs`
 * wake-ups, so on the build route the same URL flips from `building` to
 * `draft` with no navigation, and on the config route the in-flight build
 * keeps the banner current.
 */
export function useConfigPageData(
  name: string,
  buildJobId: string | undefined,
  enabled: boolean
): ConfigPageData {
  const [params] = useSearchParams()
  const requestedVersion = params.get('version')
  const wantsDraft = requestedVersion === 'draft'
  const skipAll = !enabled || !name

  const versions = useListIngestMappingConfigsQuery(
    { kind: 'mapped_tabular_ingest', name, include_archived: true, limit: 500 },
    { skip: skipAll }
  )
  const [activeLive, setActiveLive] = React.useState(false)
  const active = useGetActiveTabularConfigBuildQuery(name, {
    skip: skipAll || !!buildJobId
  })
  React.useEffect(() => {
    setActiveLive(!!active.data && isBuildInFlight(active.data))
  }, [active.data])

  const latestBuild = useGetLatestTabularConfigBuildQuery(name, {
    skip: skipAll || !!buildJobId || !wantsDraft || active.isLoading || !!active.data
  })

  const [buildLive, setBuildLive] = React.useState(true)
  React.useEffect(() => setBuildLive(true), [buildJobId])
  const build = useGetTabularConfigBuildQuery(buildJobId ?? '', {
    skip: skipAll || !buildJobId,
    refetchOnMountOrArgChange: true
  })
  React.useEffect(() => {
    if (build.data) setBuildLive(isBuildInFlight(build.data))
    // A build that cannot be loaded at all (404, 500) is not live.
    else if (build.isError) setBuildLive(false)
  }, [build.data, build.isError])

  // After *Save as vN* / a revise the build (or URL) names a version the list
  // may not have yet — its invalidated refetch lands a beat later. Holding the
  // page until then avoids a flash of the old version under a "saved" banner.
  const wantedVersion =
    (buildJobId ? build.data?.result.approvedConfig?.version : undefined) ??
    (requestedVersion !== null && Number.isSafeInteger(Number(requestedVersion))
      ? Number(requestedVersion)
      : undefined)
  const awaitingVersion =
    wantedVersion !== undefined &&
    versions.isFetching &&
    !(versions.data ?? []).some((row) => row.version === wantedVersion)

  const loading =
    versions.isLoading ||
    awaitingVersion ||
    (buildJobId ? build.isLoading && !build.data : active.isLoading) ||
    (wantsDraft && !buildJobId && !active.data && latestBuild.isLoading)

  const view = React.useMemo<ConfigView | null>(() => {
    if (loading) return null
    if (buildJobId && !build.data) return null
    return resolveConfigView({
      name,
      versions: versions.data ?? [],
      build: buildJobId ? (build.data ?? null) : null,
      active: active.data ?? null,
      latestBuild: latestBuild.data ?? null,
      requestedVersion
    })
  }, [
    loading,
    buildJobId,
    build.data,
    name,
    versions.data,
    active.data,
    latestBuild.data,
    requestedVersion
  ])

  const refetchBuild = build.refetch
  const refetchVersions = versions.refetch
  const refetchActive = active.refetch
  return {
    view,
    versions: versions.data ?? [],
    build: buildJobId ? (build.data ?? null) : (active.data ?? null),
    loading,
    error: versions.error ?? (buildJobId ? build.error : active.error),
    live: buildJobId ? buildLive && !!build.data : activeLive,
    refetchBuild: React.useCallback(() => refetchBuild(), [refetchBuild]),
    refetchAll: React.useCallback(() => {
      void refetchVersions()
      if (buildJobId) void refetchBuild()
      else void refetchActive()
    }, [buildJobId, refetchActive, refetchBuild, refetchVersions])
  }
}
